// Approval routing as User Management shows it. The server keeps one rule per person
// (kind + nature of business + sub-category group + approver); the client thinks in
// combinations — "Component · RM" — each holding any number of people. Pure, so the
// conversions are unit-tested (routing.test.js).
import { NATURE_OF_BUSINESS, SUBCATEGORY_GROUPS, natureLabel, groupLabel } from '../../utils/classification.js';

export const KINDS = ['INVITE', 'QUALITY'];

export const comboKey = (nature, sub) => `${nature || ''}|${sub || ''}`;

export const parseComboKey = (key) => {
  const [nature, sub] = String(key).split('|');
  return { nature_of_business: nature || null, supplier_subcategory: sub || null };
};

/** "Component · RM (Direct or Indirect)", "Any nature of business · HR", … */
export function comboLabel(nature, sub) {
  if (!nature && !sub) return 'Everything else (any nature, any sub-category)';
  return `${nature ? natureLabel(nature) : 'Any nature of business'} · ${sub ? groupLabel(sub) : 'Any sub-category'}`;
}

/** 3 = nature + sub-category, 2 = sub-category only, 1 = nature only, 0 = catch-all (the server's order). */
export const specificity = (nature, sub) => (sub ? 2 : 0) + (nature ? 1 : 0);

const natureOrder = (v) => (v ? NATURE_OF_BUSINESS.findIndex((o) => o.value === v) : 99);
const groupOrder = (v) => (v ? SUBCATEGORY_GROUPS.findIndex((o) => o.value === v) : 99);

/**
 * The combinations of one kind, each with its people (oldest rule first), the most specific
 * combination first — the order in which the server picks who approves.
 */
export function groupCombos(rules, kind) {
  const byKey = new Map();
  for (const r of rules || []) {
    if (r.kind !== kind) continue;
    const key = comboKey(r.nature_of_business, r.supplier_subcategory);
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        kind,
        nature_of_business: r.nature_of_business || null,
        supplier_subcategory: r.supplier_subcategory || null,
        people: [],
      });
    }
    // A VMS user (approver set), or someone without a VMS account named by address only.
    byKey.get(key).people.push({ ruleId: r.id, approver: r.approver || null, approverId: r.approver_user_id ?? null, email: r.approver_email || null });
  }
  const combos = [...byKey.values()];
  for (const c of combos) c.people.sort((a, b) => a.ruleId - b.ruleId);
  return combos.sort(
    (a, b) =>
      specificity(b.nature_of_business, b.supplier_subcategory) - specificity(a.nature_of_business, a.supplier_subcategory) ||
      natureOrder(a.nature_of_business) - natureOrder(b.nature_of_business) ||
      groupOrder(a.supplier_subcategory) - groupOrder(b.supplier_subcategory)
  );
}

/** The combination keys a user approves, per kind. */
export function combosOfUser(rules, userId) {
  const out = { INVITE: [], QUALITY: [] };
  for (const r of rules || []) {
    if (userId == null || r.approver_user_id !== userId || !out[r.kind]) continue;
    const key = comboKey(r.nature_of_business, r.supplier_subcategory);
    if (!out[r.kind].includes(key)) out[r.kind].push(key);
  }
  return out;
}

/**
 * What the Add/Edit User form must do so the user approves exactly the ticked combinations:
 * rules to create for newly ticked ones, rule ids to delete for unticked ones.
 */
export function routingChanges(rules, userId, selected) {
  const current = combosOfUser(rules, userId);
  const add = [];
  const remove = [];
  for (const kind of KINDS) {
    const want = new Set(selected?.[kind] || []);
    for (const key of want) if (!current[kind].includes(key)) add.push({ kind, ...parseComboKey(key) });
    if (userId == null) continue;
    for (const r of rules || []) {
      if (r.kind === kind && r.approver_user_id === userId && !want.has(comboKey(r.nature_of_business, r.supplier_subcategory))) remove.push(r.id);
    }
  }
  return { add, remove };
}

/** Sub-category choices for a combination. Quality only sees RM and PM: HR and Finance skip it. */
export function subcategoryOptions(kind) {
  const groups = kind === 'QUALITY' ? SUBCATEGORY_GROUPS.filter((g) => g.value === 'RM' || g.value === 'PM') : SUBCATEGORY_GROUPS;
  return [{ value: '', label: 'Any sub-category' }, ...groups];
}

export const natureOptions = () => [{ value: '', label: 'Any nature of business' }, ...NATURE_OF_BUSINESS];

/** A valid e-mail address, lower-cased — or null. */
export function cleanEmail(value) {
  const e = String(value ?? '').trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) ? e : null;
}

/** Why a named person will not be asked, or null when they will. */
export function approverProblem(approver) {
  if (!approver) return 'Unknown user';
  if (!approver.is_active) return 'Inactive — skipped';
  if (approver.role !== 'ADMIN' && approver.role !== 'COORDINATOR') return 'User role — cannot approve';
  return null;
}

export const KIND_TEXT = {
  INVITE: {
    tab: 'Invite approvers',
    title: 'Invite approvers',
    what: 'approves a new vendor invite before the registration link is sent',
    explain:
      'When someone raises an invite, the people in the most specific matching combination are e-mailed and any one of them can approve it — VMS users in Approvals, people added by e-mail through a one-time link (no VMS login). Nature + sub-category wins over sub-category only, then nature only, then "Everything else". With no match, any administrator can approve.',
    empty: 'No combinations yet — every invite waits for an administrator.',
  },
  QUALITY: {
    tab: 'Quality approvers',
    title: 'Quality approvers',
    what: 'clears an RM or PM supplier after they submit the registration form, before Level 1',
    explain:
      'After an RM or PM supplier submits the form, the people in the most specific matching combination are e-mailed and any one of them clears the supplier — VMS users in Approvals, people added by e-mail through a one-time link showing that supplier and its documents (no VMS login). HR and Finance suppliers skip this step. With no match, any administrator can clear it.',
    empty: 'No combinations yet — every RM / PM supplier waits for an administrator.',
  },
};
